class SliderViewportScaler {
  constructor(options = {}) {
    this.slider = null;
    this.rafId = null;
    this.resizeTimeout = null;
    this.resizeTimeout2 = null;
    this.lastOffset = null;
    this.lastScale = null;

    this.options = {
      sliderSelector: '.lc-slider',
      minViewportWidth: 1025,
      minViewportHeight: 600,
      minScale: 0.6,
      resizeRecheckDelay: 140,
      maxNegativeOffsetPx: 160,
      ...options,
    };

    this.handleResize = this.handleResize.bind(this);
    this.handleResizeFallback = this.handleResizeFallback.bind(this);
    this.init();
  }

  init() {
    this.slider = document.querySelector(this.options.sliderSelector);
    if (!this.slider) return;

    this.evaluate();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    if (this.resizeTimeout2) {
      clearTimeout(this.resizeTimeout2);
      this.resizeTimeout2 = null;
    }
    this.resetAdjustments();
  }

  handleResize() {
    if (this.rafId) return;
    this.rafId = window.requestAnimationFrame(() => {
      this.evaluate();
      this.rafId = null;
    });

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(this.handleResizeFallback, this.options.resizeRecheckDelay);

    if (this.resizeTimeout2) {
      clearTimeout(this.resizeTimeout2);
    }
    this.resizeTimeout2 = setTimeout(this.handleResizeFallback, this.options.resizeRecheckDelay * 3);
  }

  handleResizeFallback() {
    this.resizeTimeout = null;
    this.evaluate();
  }

  evaluate() {
    if (!this.slider) return;

    if (window.innerWidth < this.options.minViewportWidth) {
      this.resetAdjustments();
      return;
    }

    const viewportHeightRaw = window.innerHeight;
    const belowMinHeight = viewportHeightRaw < this.options.minViewportHeight;

    if (belowMinHeight && this.lastScale !== null && this.lastOffset !== null) {
      return;
    }

    const viewportHeight = belowMinHeight ? this.options.minViewportHeight : viewportHeightRaw;

    const { topEdge, bottomEdge, contentHeight } = this.measureUntransformed();

    const overflow = Math.max(0, bottomEdge - viewportHeight);
    if (overflow <= 0) {
      this.applyValues(0, 1);
      return;
    }

    const shiftUp = Math.min(overflow, this.options.maxNegativeOffsetPx);
    let offset = -shiftUp;

    const remainingOverflow = Math.max(0, overflow - shiftUp);

    const newTop = topEdge + offset;
    const allowedHeight = viewportHeight - newTop;
    const scaleNeeded = contentHeight > 0 ? allowedHeight / contentHeight : 1;
    const dynamicScale = remainingOverflow > 0
      ? Math.max(this.options.minScale, Math.min(1, scaleNeeded))
      : 1;

    this.applyValues(offset, dynamicScale);
  }

  measureUntransformed() {
    const prevOffset = this.slider.style.getPropertyValue('--lc-slider-offset');
    const prevScale = this.slider.style.getPropertyValue('--lc-slider-scale-dynamic');

    this.slider.style.setProperty('--lc-slider-offset', '0px');
    this.slider.style.setProperty('--lc-slider-scale-dynamic', '1');

    const sliderRect = this.slider.getBoundingClientRect();
    const activeInner = this.slider.querySelector('.swiper-slide-active .slide__inner');
    const activeRect = activeInner ? activeInner.getBoundingClientRect() : null;

    const topEdge = Math.min(sliderRect.top, activeRect ? activeRect.top : sliderRect.top);
    const bottomEdge = Math.max(sliderRect.bottom, activeRect ? activeRect.bottom : sliderRect.bottom);
    const contentHeight = bottomEdge - topEdge;

    if (prevOffset) {
      this.slider.style.setProperty('--lc-slider-offset', prevOffset);
    } else {
      this.slider.style.removeProperty('--lc-slider-offset');
    }
    if (prevScale) {
      this.slider.style.setProperty('--lc-slider-scale-dynamic', prevScale);
    } else {
      this.slider.style.removeProperty('--lc-slider-scale-dynamic');
    }

    return { topEdge, bottomEdge, contentHeight };
  }

  applyValues(offset, scale) {
    const offsetChanged = this.lastOffset === null || Math.abs(this.lastOffset - offset) > 0.5;
    const scaleChanged = this.lastScale === null || Math.abs(this.lastScale - scale) > 0.002;

    if (offsetChanged) {
      this.slider.style.setProperty('--lc-slider-offset', `${offset}px`);
      this.lastOffset = offset;
    }

    if (scaleChanged) {
      this.slider.style.setProperty('--lc-slider-scale-dynamic', scale.toFixed(3));
      this.lastScale = scale;
    }
  }

  resetAdjustments() {
    this.slider?.style.removeProperty('--lc-slider-offset');
    this.slider?.style.removeProperty('--lc-slider-scale-dynamic');
    this.lastOffset = null;
    this.lastScale = null;
  }
}

export default SliderViewportScaler;
